import React from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import type { AgentStatus } from '@/hooks/useMarimoSession';
import type { DemoUser } from '@/data/users';

export type AppTab = 'copilot' | 'insights' | 'maps';

const TABS: { key: AppTab; label: string }[] = [
  { key: 'copilot', label: 'Co-Pilot' },
  { key: 'insights', label: 'Insights' },
  { key: 'maps', label: 'Maps' },
];

// Project / DatasetType are now plain strings — the available set is
// determined by the active demo user (see src/data/users.ts).
export type Project = string;
export type DatasetType = string;

interface MainLayoutProps {
  children: React.ReactNode;
  isConnected: boolean;
  agents: AgentStatus[];
  onClearChat: () => void;
  onLogout?: () => void;
  onProfile?: () => void;
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  project: Project;
  onProjectChange: (p: Project) => void;
  datasetType: DatasetType;
  onDatasetTypeChange: (t: DatasetType) => void;
  // View-as user dropdown (top-right of header) + active user object.
  viewAsUsers: DemoUser[];
  viewAsKey: string;
  onViewAsChange: (key: string) => void;
  activeUser: DemoUser;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  isConnected,
  agents,
  onClearChat,
  onLogout,
  onProfile,
  activeTab,
  onTabChange,
  project,
  onProjectChange,
  datasetType,
  onDatasetTypeChange,
  viewAsUsers,
  viewAsKey,
  onViewAsChange,
  activeUser,
}) => {
  return (
    <div style={styles.container}>
      <Header
        onLogout={onLogout}
        onProfile={onProfile}
        viewAsUsers={viewAsUsers}
        viewAsKey={viewAsKey}
        onViewAsChange={onViewAsChange}
      />
      <nav style={styles.tabBar} role="tablist">
        {TABS.map((t) => {
          const isActive = t.key === activeTab;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabChange(t.key)}
              style={{
                ...styles.tab,
                ...(isActive ? styles.tabActive : {}),
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.color = '#0f172a';
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.color = '#64748b';
              }}
            >
              {t.label}
            </button>
          );
        })}
      </nav>
      <div style={styles.main}>
        <Sidebar
          isConnected={isConnected}
          agents={agents}
          onClearChat={onClearChat}
          project={project}
          onProjectChange={onProjectChange}
          datasetType={datasetType}
          onDatasetTypeChange={onDatasetTypeChange}
          activeUser={activeUser}
        />
        <main style={styles.content}>{children}</main>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  tabBar: {
    display: 'flex',
    gap: '0.25rem',
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    padding: '0 1.5rem',
    flexShrink: 0,
  },
  tab: {
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    padding: '0.75rem 1.25rem',
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#64748b',
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s',
    marginBottom: -1,
  },
  tabActive: {
    color: '#1e293b',
    borderBottomColor: '#3b82f6',
    fontWeight: 600,
  },
  main: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
};
